import { useQueryEmployeeTaxFormsChecklist } from "../../../query/employeeTaxForms/queries/useQueryEmployeeTaxFormsChecklist";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../../Onboarding/components/OnboardingFlowUtils";
import {
  OnboardingContext,
  OnboardingModuleEmployeeTaxForms
} from "../../Onboarding/types";
import { useEmployeeTaxFormsEngagementContext } from "../hooks/useEmployeeTaxFormsEngagementContext";
import { EmployeeTaxFormRenderer } from "../routes/EmployeeTaxFormRenderer";
import { EmployeeTaxFormsChecklist } from "../routes/EmployeeTaxFormsChecklist";
import { selectorShortFormTitle } from "../selectors/selectorShortFormTitle";
import { IFormChecklistItem } from "../types";

export const getEmployeeTaxFormsStepConfig = (
  _module: OnboardingModuleEmployeeTaxForms,
  _context: OnboardingContext,
  checklist?: { items: IFormChecklistItem[] }
): FlowStep | null => {
  const items = checklist?.items;
  if (!items || items.length === 0) return null;

  const allComplete = items.every(item => item.status === "Completed");

  return {
    title: "Tax form requirement",
    slug: "tax-forms",
    component: EmployeeTaxFormsChecklist,
    status: allComplete ? "Complete" : "None",
    steps: items.map(item => ({
      // Compact name ("Federal W-4") in the flow chrome — the full catalog
      // title stays on the renderer's form-overview card.
      title: selectorShortFormTitle(item.title) ?? item.title,
      // The instance id verbatim — it is opaque and case-sensitive, so no
      // toLowerCase() (unlike the old catalog formId slug).
      slug: item.id,
      component: EmployeeTaxFormRenderer,
      // The form renderer's wizard canvas (rail + content) is 944px — ask the
      // hosting Layout for the wide content column. The parent (checklist)
      // step keeps the default width.
      wideContent: true
    })),
    result: {
      Complete: {
        title: "All tax forms are submitted",
        description: "Payroll can run for this engagement."
      }
    }
  };
};

export const useEmployeeTaxFormsStep: UseCreateOnboardingStep<
  OnboardingModuleEmployeeTaxForms
> = modules => {
  const config = useOnboardingStepQueryConfig(modules, "employee_tax_forms");
  const engagementId = useEmployeeTaxFormsEngagementContext();
  // Both gates matter: the module must be present AND an engagement context
  // resolved — the checklist endpoint requires a contextId.
  const queryChecklist = useQueryEmployeeTaxFormsChecklist(engagementId, {
    enabled: config.enabled && !!engagementId
  });

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getEmployeeTaxFormsStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [queryChecklist]
    });
};
